import { Vec3 } from '../../engine/math/vec3';
import { clamp, wrapAngle, easeOutCubic } from '../../engine/math/easing';
import { sphere, combine } from '../../engine/geo/primitives';
import { translate, setEmissive, scale as scaleMesh } from '../../engine/geo/mesh';
import type { Color } from '../../engine/geo/mesh';
import type { Ctx } from '../Ctx';
import type { Player } from '../player/Player';
import { Beast } from './Beast';

const SEGMENTS = 14;
const PALE: Color = [0.82, 0.8, 0.78];

/**
 * Cistern Wyrm (§5): 14 pale spheres, blind, violet mouth. Hunts by sound (noise meter + proximity),
 * refuses to enter the Lantern's lit radius (recoils with a hiss at the edge), snaps after a 0.4 s
 * telegraph with a 4 m lunge. HP 5.
 */
export class Wyrm extends Beast {
  private readonly segs: Vec3[] = [];
  private readonly radii: number[] = [];
  private heading = 0;
  private lungeFrom = new Vec3();
  private lungeTo = new Vec3();
  private bit = false;
  private readonly home: Vec3;
  private mouthOpen = 0;
  private readonly mouth;
  /** Set by the world each step: lit lantern position and radius (or null). */
  light: { pos: Vec3; radius: number } | null = null;
  private recoilT = 0;
  private recoilDir = new Vec3();
  private heard = 0;

  constructor(ctx: Ctx, pos: Vec3) {
    super(ctx, 'wyrm', pos, 5, [0.75, 0.5, 0.95], 34);
    this.home = pos.clone();
    for (let i = 0; i < SEGMENTS; i++) {
      const r = 0.5 - i * 0.025;
      this.radii.push(r);
      const p = pos.clone(); p.z += i * r * 1.7;
      this.segs.push(p);
      const m = sphere(r, 9, 6, [PALE[0] * (1 - i * 0.015), PALE[1] * (1 - i * 0.02), PALE[2]]);
      const part = this.addPart('wyrm.seg' + i, m, this.root, new Vec3(), r);
      part.t.position.copy(p).sub(pos);
      const crack = setEmissive(translate(scaleMesh(sphere(0.06, 4, 3, [0.15, 0.1, 0.2]), 1, r * 8, 1), 0, 0, r * 0.9), [0.1, 0.05, 0.15], 0.3);
      this.addCrack('wyrm.crack' + i, crack, part.t);
    }
    // the mouth: a flattened violet sphere at the front of the head, scaled open on telegraph
    const mouthMesh = setEmissive(scaleMesh(sphere(0.3, 8, 5, [0.6, 0.3, 0.95]), 1, 0.35, 1), [0.55, 0.25, 0.95], 1.6);
    this.mouth = this.addPart('wyrm.mouth', mouthMesh, this.parts[0].t, new Vec3(0, 0, 0.42), 0.3);
  }

  protected onKnockback(dir: Vec3): void { const h = this.segs[0]; h.x += dir.x * 0.6; h.z += dir.z * 0.6; }

  private groundY(x: number, z: number, r: number): number {
    const g = this.ctx.statics.terrainHeight(x, z);
    return Math.max(g, -0.3) + r * 0.7;
  }

  private inLight(x: number, z: number): boolean {
    if (!this.light) return false;
    return Math.hypot(x - this.light.pos.x, z - this.light.pos.z) < this.light.radius - 0.5;
  }

  update(dt: number, player: Player): void {
    this.tick(dt);
    if (!this.alive) return;
    const head = this.segs[0];
    const toP = _v.copy(player.position).sub(head); toP.y = 0;
    const dist = toP.length();
    // hearing: loud actions carry far, walking is quiet, proximity always counts
    const noise = player.noiseLevel;
    const hear = (noise > 0.3 && dist < 34) || dist < 7 || (Math.hypot(player.velocity.x, player.velocity.z) > 5 && dist < 20);
    if (hear && player.state !== 'dead') this.heard = 3;
    else this.heard = Math.max(0, this.heard - dt);
    const engaged = this.heard > 0 && head.distanceTo(this.home) < this.leashRadius + 10;
    const sp = 4.5 * this.speedMul;

    if (this.recoilT > 0) {
      this.recoilT -= dt;
      head.x += this.recoilDir.x * 5 * dt; head.z += this.recoilDir.z * 5 * dt;
    } else switch (this.state) {
      case 'idle':
      case 'alert': {
        let target = engaged ? Math.atan2(toP.x, toP.z) : Math.atan2(this.home.x - head.x, this.home.z - head.z);
        const atHome = !engaged && head.distanceTo(this.home) < 3;
        if (atHome) target = this.heading + Math.sin(this.ctx.time * 0.8) * 0.8;
        this.heading += clamp(wrapAngle(target - this.heading), -3 * dt, 3 * dt);
        const speed = engaged ? sp : atHome ? 0.6 : 2.0;
        const nx = head.x + Math.sin(this.heading) * speed * dt, nz = head.z + Math.cos(this.heading) * speed * dt;
        if (this.inLight(nx, nz)) {
          // recoil from the light's edge with a hiss
          this.recoilT = 0.7;
          this.recoilDir.set(-Math.sin(this.heading), 0, -Math.cos(this.heading));
          this.ctx.sfx.wyrmHiss(head);
          this.mouthOpen = 1;
        } else {
          head.x = nx; head.z = nz;
          const pushed = this.ctx.statics.resolveCapsule(head, 0.45, 0.8, 0.2, _push);
          if (pushed) this.heading += 1.5 * dt * (Math.random() > 0.5 ? 1 : -1);
        }
        this.state = engaged ? 'alert' : 'idle';
        if (engaged && dist < 4.5 && !this.inLight(player.position.x, player.position.z)) {
          this.setState('telegraph');
          this.ctx.sfx.telegraph('wyrm', head, 0.4);
        }
        break;
      }
      case 'telegraph': {
        this.mouthOpen = clamp(this.stateT / 0.4, 0, 1);
        this.heading = Math.atan2(toP.x, toP.z);
        if (this.stateT >= 0.4) {
          this.setState('attack');
          this.lungeFrom.copy(head);
          this.lungeTo.copy(head).addScaled(_d.set(Math.sin(this.heading), 0, Math.cos(this.heading)), 4);
          this.bit = false;
        }
        break;
      }
      case 'attack': {
        const k = clamp(this.stateT / 0.25, 0, 1), e = easeOutCubic(k);
        head.x = this.lungeFrom.x + (this.lungeTo.x - this.lungeFrom.x) * e;
        head.z = this.lungeFrom.z + (this.lungeTo.z - this.lungeFrom.z) * e;
        this.ctx.statics.resolveCapsule(head, 0.45, 0.8, 0.2);
        if (!this.bit && k > 0.3) {
          const c = _d.copy(head); c.y = player.position.y + 0.5;
          if (this.tryHitPlayer(player, c, 0.9, 2)) this.bit = true;
        }
        if (k >= 1) this.setState('recover');
        break;
      }
      case 'recover': {
        this.mouthOpen = Math.max(0, 1 - this.stateT * 2);
        if (this.stateT >= 0.8) this.setState('alert');
        break;
      }
      default: break;
    }
    if (this.state !== 'telegraph' && this.state !== 'recover' && this.recoilT <= 0) this.mouthOpen = Math.max(0, this.mouthOpen - dt * 2);

    // chain follow
    for (let i = 1; i < SEGMENTS; i++) {
      const prev = this.segs[i - 1], cur = this.segs[i];
      const rest = (this.radii[i - 1] + this.radii[i]) * 0.9;
      const d = _d.copy(cur).sub(prev); d.y = 0;
      const l = d.length();
      if (l > rest) { d.scale(rest / l); cur.x = prev.x + d.x; cur.z = prev.z + d.z; }
    }
    const t = this.ctx.time;
    head.y = this.groundY(head.x, head.z, this.radii[0]) + (this.state === 'telegraph' ? 0.4 : 0.15);
    for (let i = 1; i < SEGMENTS; i++) this.segs[i].y = this.groundY(this.segs[i].x, this.segs[i].z, this.radii[i]) + Math.abs(Math.sin(t * 4 + i * 0.7)) * 0.12;
    this.root.position.copy(head);
    for (let i = 0; i < SEGMENTS; i++) {
      const p = this.parts[i];
      p.t.position.copy(this.segs[i]).sub(head);
      if (i === 0) p.t.rotation.setEuler(this.heading, this.state === 'telegraph' ? -0.5 : 0, 0);
      else { const nx = this.segs[i - 1]; p.t.rotation.setYaw(Math.atan2(nx.x - this.segs[i].x, nx.z - this.segs[i].z)); }
    }
    this.mouth.t.scale.set(1 + this.mouthOpen * 0.4, 0.4 + this.mouthOpen * 2.2, 1 + this.mouthOpen * 0.3);
    this.mouth.rd.emissiveMul = 0.6 + this.mouthOpen * 1.2;
  }
}
const _v = new Vec3(), _d = new Vec3(), _push = new Vec3();
